import React, { useEffect, useMemo, useState } from "react";
import { FaSearch, FaLock, FaExclamationTriangle } from "react-icons/fa";
import "./LinkFamilyMemberModal.css";

/**
 * LinkFamilyMemberModal — staff-routine "Link Family Member" surface.
 *
 * Customer search REUSES the parent page's deployed search pattern
 * (staffAPI.searchCustomers). Reason field is optional. Inline error
 * banner surfaces backend rejection codes:
 *  - CORP_FAMILY_COOLDOWN_ACTIVE (gold; informational with cooldownEnd date)
 *  - CORP_FAMILY_LINK_OCCUPIED (red; slot occupied — race / double-tab)
 *  - CORP_FAMILY_CUSTOMER_ALREADY_LINKED (red; customer linked elsewhere)
 *
 * Cooldown rejection is the load-bearing musical-chairs prevention UX
 * (Flow B / Zuri spec): cite the date, cite ADR-037, show the prior
 * family-member name, no "support escalation" copy.
 *
 * Props:
 *  - isOpen: boolean
 *  - enrollment: { _id, user: { firstName, lastName, email } } — Alice's row
 *  - onClose: () => void
 *  - onConfirm: ({ familyMemberUserId, reason }) => void
 *  - inFlight: boolean
 *  - error: { code, message, cooldownEnd?, priorFamilyMemberName?, existingEnrollmentEmployeeName?, existingFamilyMemberName? } | null
 *  - onErrorCleared: () => void
 *  - searchCustomers: (query) => Promise<{ data: { customers: [...] }}>  — REUSED parent API
 */
const formatCooldownDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "America/Phoenix",
    }).concat(" MST");
  } catch (_err) {
    return iso;
  }
};

const LinkFamilyMemberModal = ({
  isOpen,
  enrollment,
  onClose,
  onConfirm,
  inFlight = false,
  error = null,
  onErrorCleared,
  searchCustomers,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [reason, setReason] = useState("");

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchValue("");
      setSearchResults([]);
      setSelectedCustomer(null);
      setReason("");
      setSearchError("");
    }
  }, [isOpen]);

  const employeeName = useMemo(() => {
    if (!enrollment?.user) return "this enrollment";
    return `${enrollment.user.firstName || ""} ${enrollment.user.lastName || ""}`.trim() || "this enrollment";
  }, [enrollment]);

  const handleSearch = async () => {
    if (!searchValue || searchValue.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    if (!searchCustomers) {
      setSearchError("Search not available.");
      return;
    }
    try {
      setSearchLoading(true);
      setSearchError("");
      const response = await searchCustomers({ search: searchValue.trim() });
      setSearchResults(response?.data?.customers || []);
    } catch (_err) {
      setSearchError("Unable to search members.");
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelect = (customer) => {
    setSelectedCustomer(customer);
    if (error && onErrorCleared) onErrorCleared();
  };

  const handleConfirm = () => {
    if (!selectedCustomer || inFlight) return;
    onConfirm?.({
      familyMemberUserId: selectedCustomer._id,
      reason: reason.trim() || undefined,
    });
  };

  const handleBackdropClick = () => {
    if (inFlight) return;
    onClose?.();
  };

  // Esc closes
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (event) => {
      if (event.key === "Escape" && !inFlight) {
        onClose?.();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, inFlight, onClose]);

  if (!isOpen) return null;

  const renderErrorBanner = () => {
    if (!error) return null;
    const isCooldown = error.code === "CORP_FAMILY_COOLDOWN_ACTIVE";
    const variantClass = isCooldown
      ? "link-fm-modal-error is-cooldown"
      : "link-fm-modal-error is-rejection";
    const IconCmp = isCooldown ? FaLock : FaExclamationTriangle;
    let title;
    let body;
    let subtext;
    if (isCooldown) {
      title = "Family-member link is locked";
      const cooldownDate = formatCooldownDate(error.cooldownEnd);
      const priorName = error.priorFamilyMemberName || "the prior linked customer";
      body = `${employeeName}'s enrollment was last unlinked from ${priorName}. Per the 90-day cooldown rule (ADR-037), a new family member cannot be linked until ${cooldownDate}.`;
      subtext = "This rule prevents rotation of the corp benefit between multiple customers. Contact Roderick if an exception is required.";
    } else if (error.code === "CORP_FAMILY_LINK_OCCUPIED") {
      title = "Family member already linked";
      const existing = error.existingFamilyMemberName || "another family member";
      body = `${existing} is already linked to ${employeeName}'s enrollment. Remove first to link a new family member.`;
      subtext = "Use the Remove action on the enrollment row, then re-open this dialog.";
    } else if (error.code === "CORP_FAMILY_CUSTOMER_ALREADY_LINKED") {
      title = "Customer already linked";
      const other = error.existingEnrollmentEmployeeName || "another active corp enrollment";
      body = `${selectedCustomer ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}` : "This customer"} is already linked as ${other}'s family member. A customer can only be the family member of one active corp enrollment.`;
      subtext = `Remove from ${other}'s enrollment first if reassigning.`;
    } else {
      title = "Unable to link family member";
      body = error.message || "The server rejected the link request. Please try again.";
      subtext = "";
    }
    return (
      <div className={variantClass} role="alert">
        <span className="link-fm-modal-error-icon" aria-hidden="true">
          <IconCmp />
        </span>
        <div className="link-fm-modal-error-body">
          <strong className="link-fm-modal-error-title">{title}</strong>
          <p className="link-fm-modal-error-text">{body}</p>
          {subtext ? <p className="link-fm-modal-error-subtext">{subtext}</p> : null}
        </div>
      </div>
    );
  };

  return (
    <div
      className="link-fm-modal-backdrop"
      onMouseDown={handleBackdropClick}
      role="presentation"
    >
      <div
        className="link-fm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="link-fm-modal-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="link-fm-modal-header">
          <h2 id="link-fm-modal-title" className="link-fm-modal-title">
            Link Family Member
          </h2>
          <p className="link-fm-modal-subtitle">
            Linked customer gets the corporate rate. One family member max per enrollment.
          </p>
        </div>

        {renderErrorBanner()}

        <label htmlFor="link-fm-search-input" className="link-fm-modal-label">
          Search members
        </label>
        <div className="link-fm-search-row">
          <input
            id="link-fm-search-input"
            type="text"
            className="link-fm-search-input"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder="Search by name, email, or phone"
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleSearch();
              }
            }}
            disabled={inFlight}
            autoFocus
          />
          <button
            type="button"
            className="link-fm-search-button"
            onClick={handleSearch}
            disabled={inFlight || searchLoading}
          >
            <FaSearch aria-hidden="true" />
            <span>{searchLoading ? "Searching..." : "Search"}</span>
          </button>
        </div>

        {searchError ? (
          <p className="link-fm-modal-search-error" role="alert">
            {searchError}
          </p>
        ) : null}

        {searchResults.length > 0 ? (
          <div className="link-fm-search-results" role="listbox" aria-label="Search results">
            {searchResults.map((customer) => {
              const isSelected = selectedCustomer?._id === customer._id;
              return (
                <button
                  type="button"
                  key={customer._id}
                  className={`link-fm-search-result${isSelected ? " is-selected" : ""}`}
                  onClick={() => handleSelect(customer)}
                  disabled={inFlight}
                  role="option"
                  aria-selected={isSelected}
                >
                  <span className="link-fm-search-result-name">
                    {customer.firstName} {customer.lastName}
                  </span>
                  <span className="link-fm-search-result-email">
                    {customer.email}
                  </span>
                </button>
              );
            })}
          </div>
        ) : searchValue && !searchLoading ? (
          <p className="link-fm-modal-empty-results">
            No matching members found.
          </p>
        ) : null}

        <label htmlFor="link-fm-reason-input" className="link-fm-modal-label">
          Reason (optional)
        </label>
        <p className="link-fm-modal-helper">
          Captured in the audit trail (e.g. &quot;spouse per HR contact&quot;).
        </p>
        <input
          id="link-fm-reason-input"
          type="text"
          className="link-fm-reason-input"
          value={reason}
          onChange={(event) => {
            if (event.target.value.length <= 250) {
              setReason(event.target.value);
            }
          }}
          placeholder="e.g. spouse per HR contact"
          maxLength={250}
          disabled={inFlight}
        />

        <div className="link-fm-modal-footer">
          <button
            type="button"
            className="link-fm-modal-cancel"
            onClick={onClose}
            disabled={inFlight}
          >
            Cancel
          </button>
          <button
            type="button"
            className="link-fm-modal-confirm"
            onClick={handleConfirm}
            disabled={!selectedCustomer || inFlight}
            aria-disabled={!selectedCustomer || inFlight}
          >
            {inFlight ? (
              <span className="link-fm-modal-spinner" aria-label="Submitting" />
            ) : (
              "Link"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkFamilyMemberModal;
